import type { FlowStep } from '@/shared/api'

/**
 * Ход брони (3.1) — состояние конкретной брони на сценарии.
 *
 * ⚠️ СПИСОК ПЛОСКИЙ ПО УСЛОВИЮ. Сервер умеет отдавать дорожку и структурно (отрезки плюс участки
 * ветвления), но макет держит вертикальный список «Ход брони» (`prototype/Prototype.dc.html:2321`),
 * и спека разрешает этот выбор ТОЛЬКО если сказать, что список приблизителен: при ветвлении шаг,
 * мимо которого бронь уже прошла, может показываться как «впереди». Поэтому `branching` — причина
 * показать плашку словами, а не бейдж «ветвление».
 */

/**
 * Подпись типа шага под названием — или ничего, если название уже про то же.
 *
 * Правило из макета (`showKind`): тип показываем, только если название его не содержит, сравнение
 * без регистра. Менеджер называет шаги своими словами, и у половины название и есть тип.
 *
 * Сравнение по ПОДСТРОКЕ намеренно: «Предоплата 30%» проглотит тип «Оплата», и это верно по сути —
 * разбор на слова ради такого случая стоил бы больше самого случая.
 */
export function stepKindNote(title: string, kindLabel: string | undefined): string | undefined {
  const kind = kindLabel?.trim()
  if (!kind) return undefined
  return title.toLowerCase().includes(kind.toLowerCase()) ? undefined : kind
}

/**
 * Название этапа, С КОТОРОГО список перестаёт быть точным. Без него плашка «дальше приблизительно»
 * предупреждает обо всём сразу и потому ни о чём.
 *
 * ⚠️ СОСТОЯНИЕ В ПРЕДИКАТЕ НЕСУЩЕЕ. Плоский список несёт этапы ВСЕХ веток, включая пройденную: её
 * шаги помечены веткой и давно `done`. Искать «первый с веткой» значило бы за первой же развилкой
 * показать пальцем назад, на зелёную строку, и объявить приблизительным достоверное. Приблизительно
 * только будущее, поэтому граница начинается с `pending`.
 *
 * `undefined`, когда ветвление есть, а границу сервер не назвал: плашка остаётся общей. Назвать
 * место наугад хуже — менеджер поверит.
 */
export function flowApproxFrom(steps: ReadonlyArray<FlowStep>): string | undefined {
  return steps.find((step) => step.branchLabel && step.state === 'pending')?.title
}

/**
 * Номер текущего этапа и сколько всего — ПО САМОМУ СПИСКУ, который менеджер видит.
 *
 * ⚠️ ВТОРОГО ПРОСТРАНСТВА СЧЁТА НЕТ. Контракт определяет `currentStep` как номер среди `steps`;
 * спека (3.1) называет приблизительным СПИСОК, а не номер. Взяв номер из другого счёта, экран
 * напечатает «Шаг 5 из 7», где 5 и 7 из разных пространств, а `waitingFor` назовёт строку 4.
 */
export function flowPosition(steps: ReadonlyArray<FlowStep>): { step: number; total: number } | null {
  const index = steps.findIndex((step) => step.state === 'current')
  return index === -1 ? null : { step: index + 1, total: steps.length }
}
